/**
 * @graphql cc
 *
 * Triggers a "move to personal space" flag when content is newly created
 * in a non-personal space via the global create dropdown. This component renders
 * null and should be placed inside the content view/editor area.
 *
 * Supports pages, live docs, blogposts, and whiteboards. Each content type
 * has its own create experience (e.g. CREATE_PAGE_EXPERIENCE, CREATE_WHITEBOARD_EXPERIENCE)
 * which is used to detect "newly created" state.
 */
import type { ComponentType } from 'react';
import { useEffect, useRef, useCallback } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { preloadPersonalSpaceDetailsQuery$data } from '@confluence/space-utils/entry-points/index.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	CREATE_PAGE_EXPERIENCE,
	CREATE_WHITEBOARD_EXPERIENCE,
	CREATE_DATABASE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { useExperienceState } from '@confluence/experience-tracker/entry-points/useExperienceState.ts';
import {
	VIEW_PAGE,
	EDIT_PAGE_V2,
	EDIT_BLOG_V2,
	WHITEBOARD_BOARD,
	DATABASE_VIEW,
} from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { preloadPersonalSpaceDetailsQuery } from '@confluence/space-utils/entry-points/index.ts';

import type {
	MoveToPersonalSpaceFlagTriggerMovePageAppendMutation$data,
	MoveToPersonalSpaceFlagTriggerMovePageAppendMutation$variables,
} from './__generated__/MoveToPersonalSpaceFlagTriggerMovePageAppendMutation.graphql.ts';
import type {
	MoveToPersonalSpaceFlagTriggerMovePageTopLevelMutation$data,
	MoveToPersonalSpaceFlagTriggerMovePageTopLevelMutation$variables,
} from './__generated__/MoveToPersonalSpaceFlagTriggerMovePageTopLevelMutation.graphql.ts';
import type { MoveToPersonalSpaceFlagTriggerSpaceHomepageQuery$data } from './__generated__/MoveToPersonalSpaceFlagTriggerSpaceHomepageQuery.graphql.ts';
import type {
	MoveToPersonalSpaceFlagTriggerMoveBlogMutation$data,
	MoveToPersonalSpaceFlagTriggerMoveBlogMutation$variables,
} from './__generated__/MoveToPersonalSpaceFlagTriggerMoveBlogMutation.graphql.ts';

const MOVE_TO_PERSONAL_SPACE_FLAG_ID = 'move-to-personal-space-flag';
// All global create dropdown flows share this prefix in their `source` URL param.
const GLOBAL_CREATE_DROPDOWN_PREFIX = 'globalCreateDropdown';

/**
 * Returns the create experience name for the given content type.
 * Each content type has its own tracked experience during creation.
 */
function getCreateExperienceName(contentType: string): string {
	switch (contentType) {
		case 'page':
		case 'blogpost':
			return CREATE_PAGE_EXPERIENCE;
		case 'whiteboard':
			return CREATE_WHITEBOARD_EXPERIENCE;
		case 'database':
			return CREATE_DATABASE_EXPERIENCE;
		default:
			return '';
	}
}

function getPostMoveUrl(
	contentType: string,
	spaceKey: string,
	contentId: string,
	isLivePage: boolean = false,
): string {
	if (isLivePage) {
		return VIEW_PAGE.toUrl({ spaceKey, contentId });
	}
	switch (contentType) {
		case 'blogpost':
			return EDIT_BLOG_V2.toUrl({ spaceKey, contentType: 'blog', contentId });
		case 'whiteboard':
			return WHITEBOARD_BOARD.toUrl({ spaceKey, contentId });
		case 'database':
			return DATABASE_VIEW.toUrl({ spaceKey, contentId });
		default:
			return EDIT_PAGE_V2.toUrl({ spaceKey, contentType: 'pages', contentId });
	}
}

// Module-level set to track which contentIds have already shown the flag this session.
// Prevents re-showing after component remounts (e.g. navigation away and back).
// Resets on full page refresh — same lifecycle as the experience tracker state.
const shownContentIds = new Set<string>();

// Exported for testing purposes only — allows tests to clear the set between test cases.
export const shownContentIdsForTesting: Set<string> = shownContentIds;

/**
 * Returns an ICU select key for the given content type.
 * Used as the {contentType} variable in i18n messages that use ICU select syntax
 * so translators can provide locale-specific labels for each content type.
 */
function getContentTypeKey(contentType: string, isLivePage?: boolean): string {
	if (isLivePage) {
		return 'liveDoc';
	}
	switch (contentType) {
		case 'page':
			return 'page';
		case 'blogpost':
			return 'blog';
		case 'whiteboard':
			return 'whiteboard';
		case 'database':
			return 'database';
		default:
			return 'other';
	}
}

const i18nMessages = defineMessages({
	flagTitle: {
		id: 'content-types-utils.move-to-personal-space-flag.title',
		defaultMessage: 'Do you want to move this to your own space?',
		description:
			'Title of a flag shown after content is created in a space that is not the users personal space',
	},
	flagDescription: {
		id: 'content-types-utils.move-to-personal-space-flag.description',
		defaultMessage:
			'This {contentType, select, page {page} blog {blog} whiteboard {whiteboard} database {database} liveDoc {live doc} other {content}} was created in a shared space. You can move it to your personal space instead.',
		description: 'Description of a flag shown after content is created in a shared space',
	},
	moveAction: {
		id: 'content-types-utils.move-to-personal-space-flag.move-action',
		defaultMessage: 'Move to my space',
		description: 'Button label to move content to personal space',
	},
	keepAction: {
		id: 'content-types-utils.move-to-personal-space-flag.keep-action',
		defaultMessage: 'Keep here',
		description: 'Button label to dismiss the suggestion',
	},
	moveSuccessTitle: {
		id: 'content-types-utils.move-to-personal-space-flag.move-success-title',
		defaultMessage:
			'{contentType, select, page {Page} blog {Blog} whiteboard {Whiteboard} database {Database} liveDoc {Live doc} other {Content}} moved to your space',
		description: 'Title of a success flag shown after content is moved',
	},
	moveErrorTitle: {
		id: 'content-types-utils.move-to-personal-space-flag.move-error-title',
		defaultMessage:
			"Couldn't move {contentType, select, page {page} blog {blog} whiteboard {whiteboard} database {database} liveDoc {live doc} other {content}}",
		description: 'Title of an error flag shown when move fails',
	},
	moveErrorDescription: {
		id: 'content-types-utils.move-to-personal-space-flag.move-error-description',
		defaultMessage: 'Something went wrong. Please try moving it manually.',
		description: 'Description of an error flag shown when move fails',
	},
});

const MOVE_PAGE_APPEND_MUTATION = graphql`
	mutation MoveToPersonalSpaceFlagTriggerMovePageAppendMutation($pageId: ID!, $parentId: ID!) {
		movePageAppend(input: { pageId: $pageId, parentId: $parentId }) {
			page {
				id
			}
		}
	}
`;

const MOVE_PAGE_TOP_LEVEL_MUTATION = graphql`
	mutation MoveToPersonalSpaceFlagTriggerMovePageTopLevelMutation(
		$pageId: ID!
		$spaceKey: String!
	) {
		movePageTopLevel(input: { pageId: $pageId, targetSpaceKey: $spaceKey }) {
			page {
				id
			}
		}
	}
`;

const MOVE_BLOG_MUTATION = graphql`
	mutation MoveToPersonalSpaceFlagTriggerMoveBlogMutation($blogPostId: Long!, $spaceId: Long!) {
		moveBlog(input: { blogPostId: $blogPostId, targetSpaceId: $spaceId }) {
			content {
				id
				space {
					key
					alias
				}
			}
		}
	}
`;

const SPACE_HOMEPAGE_QUERY = graphql`
	query MoveToPersonalSpaceFlagTriggerSpaceHomepageQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			homepage {
				id
			}
		}
	}
`;

type MoveToPersonalSpaceFlagTriggerProps = {
	contentId: string;
	contentType: string;
	isLivePage?: boolean;
	spaceKey: string | undefined;
	flags: FlagsStateContainer;
};

const MoveToPersonalSpaceFlagTriggerComponent = ({
	contentId,
	contentType,
	isLivePage,
	spaceKey,
	flags,
}: MoveToPersonalSpaceFlagTriggerProps) => {
	const { formatMessage } = useIntl();
	const contentTypeKey = getContentTypeKey(contentType, isLivePage);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { push } = useRouteActions();

	const createExperienceName = getCreateExperienceName(contentType);
	const createExperienceState = useExperienceState(createExperienceName);
	// True if content was just created via the global create dropdown and the experience
	// has not failed or been aborted. Excludes page tree and space-level contextual creates.
	// Note: for pages/blogs the experience stops with taskSuccess before the editor mounts,
	// so we allow taskSuccess stops and only guard against taskFail and taskAbort.
	const isNewlyGloballyCreated =
		!!createExperienceState &&
		createExperienceState.stopState !== 'taskFail' &&
		createExperienceState.stopState !== 'taskAbort' &&
		(createExperienceState.attributes?.flow as string)?.startsWith(GLOBAL_CREATE_DROPDOWN_PREFIX);

	// Re-use the preloaded personal space query — this data is preloaded during
	// preloadHomeRoute so it should be available in cache instantly.
	// Skip the query entirely when the page is not newly created to avoid
	// unnecessary network requests on regular page visits.
	const { data: personalSpaceDetailsData, loading } =
		useQueryInterop<preloadPersonalSpaceDetailsQuery$data>(preloadPersonalSpaceDetailsQuery, {
			skip: !isNewlyGloballyCreated,
		});

	// If the user has a personal space, space will be non-null with a key and id.
	// If personal spaces are disabled or user hasn't created one, space will be null.
	const personalSpaceKey = personalSpaceDetailsData?.user?.confluence?.space?.key ?? null;
	const personalSpaceId = personalSpaceDetailsData?.user?.confluence?.space?.id ?? null;
	const hasPersonalSpace = personalSpaceKey !== null && personalSpaceId !== null;

	// Fetch the homepage (overview) page of the user's personal space so we can
	// move the content under it instead of at the space root level
	const { data: homepageData, loading: homepageLoading } =
		useQueryInterop<MoveToPersonalSpaceFlagTriggerSpaceHomepageQuery$data>(SPACE_HOMEPAGE_QUERY, {
			variables: { spaceKey: personalSpaceKey || '' },
			skip: !isNewlyGloballyCreated || !hasPersonalSpace,
		});
	const personalSpaceHomepageId = homepageData?.space?.homepage?.id ?? null;

	const [movePageAppend] = useMutationInterop<
		MoveToPersonalSpaceFlagTriggerMovePageAppendMutation$data,
		MoveToPersonalSpaceFlagTriggerMovePageAppendMutation$variables
	>(MOVE_PAGE_APPEND_MUTATION);
	const [movePageTopLevel] = useMutationInterop<
		MoveToPersonalSpaceFlagTriggerMovePageTopLevelMutation$data,
		MoveToPersonalSpaceFlagTriggerMovePageTopLevelMutation$variables
	>(MOVE_PAGE_TOP_LEVEL_MUTATION);
	const [moveBlog] = useMutationInterop<
		MoveToPersonalSpaceFlagTriggerMoveBlogMutation$data,
		MoveToPersonalSpaceFlagTriggerMoveBlogMutation$variables
	>(MOVE_BLOG_MUTATION);

	// Use refs to avoid stale closures in flag action callbacks
	const personalSpaceKeyRef = useRef(personalSpaceKey);
	personalSpaceKeyRef.current = personalSpaceKey;
	const personalSpaceIdRef = useRef(personalSpaceId);
	personalSpaceIdRef.current = personalSpaceId;
	const personalSpaceHomepageIdRef = useRef(personalSpaceHomepageId);
	personalSpaceHomepageIdRef.current = personalSpaceHomepageId;

	const moveContent = useCallback(async (): Promise<string | null> => {
		const homepageId = personalSpaceHomepageIdRef.current;
		const targetSpaceId = personalSpaceIdRef.current;
		const targetSpaceKey = personalSpaceKeyRef.current;

		switch (contentType) {
			case 'blogpost': {
				if (!targetSpaceId) {
					return null;
				}
				// blogPostId and spaceId are typed as Long! but come as strings — cast as any
				// to match the pattern used in MoveBlogModal
				const moveBlogResult = await moveBlog({
					variables: { blogPostId: contentId as any, spaceId: targetSpaceId as any },
				});
				return moveBlogResult?.data?.moveBlog?.content?.id ?? null;
			}
			case 'database':
			case 'whiteboard':
			case 'page': {
				if (homepageId) {
					// Move under the overview/homepage page if it exists
					const movePageAppendResult = await movePageAppend({
						variables: { pageId: contentId, parentId: homepageId },
					});
					return movePageAppendResult?.data?.movePageAppend?.page?.id ?? null;
				}
				if (!targetSpaceKey) {
					return null;
				}
				// Fall back to top-level move if no homepage exists
				const movePageTopLevelResult = await movePageTopLevel({
					variables: { pageId: contentId, spaceKey: targetSpaceKey },
				});
				return movePageTopLevelResult?.data?.movePageTopLevel?.page?.id ?? null;
			}
			default:
				return null;
		}
	}, [contentType, contentId, moveBlog, movePageAppend, movePageTopLevel]);

	const handleMoveToPersonalSpace = useCallback(async () => {
		const targetSpaceKey = personalSpaceKeyRef.current;

		void flags.hideFlag(MOVE_TO_PERSONAL_SPACE_FLAG_ID);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'moveToPersonalSpaceConfirm',
				source: 'moveToPersonalSpaceFlag',
				attributes: {
					contentId,
					createdInSpaceKey: spaceKey,
					targetSpaceKey,
				},
			},
		}).fire();

		try {
			const movedPageId = await moveContent();

			if (!movedPageId || !targetSpaceKey) {
				throw new Error('Missing move result or target space key');
			}

			void flags.showSuccessFlag({
				id: 'move-to-personal-space-success-flag',
				title: formatMessage(i18nMessages.moveSuccessTitle, {
					contentType: contentTypeKey,
				}),
				isAutoDismiss: true,
			});
			push(getPostMoveUrl(contentType, targetSpaceKey, movedPageId, isLivePage));
		} catch {
			void flags.showErrorFlag({
				id: 'move-to-personal-space-error-flag',
				title: formatMessage(i18nMessages.moveErrorTitle, { contentType: contentTypeKey }),
				description: formatMessage(i18nMessages.moveErrorDescription),
				isAutoDismiss: true,
			});
		}
	}, [
		flags,
		formatMessage,
		createAnalyticsEvent,
		moveContent,
		push,
		contentId,
		spaceKey,
		contentType,
		isLivePage,
		contentTypeKey,
	]);

	const handleKeepHere = useCallback(() => {
		void flags.hideFlag(MOVE_TO_PERSONAL_SPACE_FLAG_ID);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'moveToPersonalSpaceDismiss',
				source: 'moveToPersonalSpaceFlag',
				attributes: {
					contentId,
					createdInSpaceKey: spaceKey,
				},
			},
		}).fire();
	}, [flags, createAnalyticsEvent, contentId, spaceKey]);

	const showFlag = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'moveToPersonalSpaceFlagScreen',
				attributes: {
					contentId,
					createdInSpaceKey: spaceKey,
				},
			},
		}).fire();

		void flags.showInfoFlag({
			id: MOVE_TO_PERSONAL_SPACE_FLAG_ID,
			title: formatMessage(i18nMessages.flagTitle),
			description: formatMessage(i18nMessages.flagDescription, { contentType: contentTypeKey }),
			actions: [
				{
					content: formatMessage(i18nMessages.moveAction),
					onClick: handleMoveToPersonalSpace,
				},
				{
					content: formatMessage(i18nMessages.keepAction),
					onClick: handleKeepHere,
				},
			],
		});
	}, [
		flags,
		formatMessage,
		createAnalyticsEvent,
		contentId,
		spaceKey,
		handleMoveToPersonalSpace,
		handleKeepHere,
		contentTypeKey,
	]);

	useEffect(() => {
		// Only trigger for newly created pages, and wait for all data to be ready
		if (!isNewlyGloballyCreated || loading || homepageLoading || !contentId || !spaceKey) {
			return;
		}

		// Don't show duplicate flags for the same contentId (survives remounts via module-level Set)
		if (shownContentIds.has(contentId)) {
			return;
		}

		// Don't show if user doesn't have a personal space (personal spaces disabled)
		if (!hasPersonalSpace) {
			return;
		}

		// Don't show if content was created in a personal space (starts with ~)
		if (spaceKey.startsWith('~')) {
			return;
		}

		// Don't show if content was created in the user's personal space
		if (spaceKey === personalSpaceKey) {
			return;
		}

		shownContentIds.add(contentId);
		showFlag();
	}, [
		isNewlyGloballyCreated,
		loading,
		homepageLoading,
		contentId,
		spaceKey,
		hasPersonalSpace,
		personalSpaceKey,
		showFlag,
	]);

	return null;
};

export const MoveToPersonalSpaceFlagTrigger: ComponentType<
	Omit<MoveToPersonalSpaceFlagTriggerProps, 'flags'>
> = withFlags(MoveToPersonalSpaceFlagTriggerComponent);
